import { ComponentType, FC } from 'react';
import classNames from 'classnames';
import { toLower } from 'lodash';

import { isColorBright, resolveColorProperty } from '@gn-audio/jss-components/utils';

import { getSharedProperty } from 'common/getters';
import { IJabraTheme } from 'common/jabraTheme/JabraTheme.models';
import { JabraThemeProvider, useJabraTheme } from 'common/jabraTheme/JabraThemeContext';
import { useJabraThemesList } from 'common/jabraTheme/JabraThemesListContext';
import { ComponentProps } from 'lib/component-props';

export interface ThemedOptions {
  defaultTheme?: IJabraTheme;
  legacyBackgroundFieldName?: string;
}
export const withTheme = <P extends ComponentProps>({
  legacyBackgroundFieldName,
  defaultTheme,
}: ThemedOptions = {}) => {
  return (WrappedComponent: ComponentType<P>): FC<P> => {
    const ThemedComponent = (props: P) => {
      const parentTheme = useJabraTheme();
      const themes = useJabraThemesList();
      let finalTheme = defaultTheme;
      if (parentTheme) {
        finalTheme = parentTheme;
      } else {
        const legacyBg =
          !!props.rendering.fields && legacyBackgroundFieldName
            ? getSharedProperty(props.rendering.fields as any, legacyBackgroundFieldName)?.value
            : undefined;
        const legacyBgHex = legacyBg && resolveColorProperty(legacyBg);

        const currentThemeName = props.params?.['Theme'];
        let jabraTheme = themes.find(
          (themeItem) => toLower(themeItem?.name) === toLower(currentThemeName)
        );

        if (legacyBgHex) {
          const isDark = !isColorBright(legacyBgHex);
          finalTheme = {
            backgroundColor: legacyBgHex,
            darkTheme: isDark,
            foregroundColor: isDark ? 'white' : 'black',
          };
        }

        if (jabraTheme?.backgroundColor) {
          finalTheme = {
            darkTheme: jabraTheme.darkTheme === null ? false : jabraTheme.darkTheme.value,
            backgroundColor: jabraTheme.backgroundColor?.value,
            foregroundColor: jabraTheme.foregroundColor?.value,
          };
        }
      }

      const styles = {} as any;

      const { backgroundColor, foregroundColor, darkTheme = false } = finalTheme ?? {};
      if (backgroundColor) {
        styles['--bgColor'] = backgroundColor;
      }
      if (foregroundColor) {
        styles['--fgColor'] = foregroundColor;
      }
      return (
        <div
          className={classNames('themed-container', darkTheme ? 'dark' : 'light')}
          style={styles}
        >
          <JabraThemeProvider theme={finalTheme}>
            <WrappedComponent {...props} />
          </JabraThemeProvider>
        </div>
      );
    };
    ThemedComponent.displayName = 'ThemedComponent';
    return ThemedComponent;
  };
};
